import React, { useState, useEffect } from 'react';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import { Container, Typography, Box, Grid, CircularProgress, Chip } from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import InfoIcon from '@mui/icons-material/Info';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface Announcement {
  id: string;
  title: string;
  description: string;
  date: string;
  priority: 'high' | 'medium' | 'low';
}

export default function Announcements() {
  const defaultAnnouncements: Announcement[] = [
    { id: '1', title: 'Maha Shivaratri Special Pooja Schedule', description: 'Special Abhishekam and Rudrabhishekam will be conducted on Maha Shivaratri. Devotees are requested to register in advance. The temple will remain open throughout the night. Special prasadam distribution will be arranged.', date: '2026-02-20', priority: 'high' },
    { id: '2', title: 'Temple Timing Change - Festival Season', description: 'During the festival season (Feb 15 - Mar 15), temple timings will be extended. Morning: 4:30 AM - 12:30 PM, Evening: 4:00 PM - 9:30 PM. Devotees are requested to plan their visits accordingly.', date: '2026-02-12', priority: 'high' },
    { id: '3', title: 'Annadanam Hall Renovation Complete', description: 'The renovation of the Annadanam Hall has been completed. The new hall can now accommodate 500 devotees at a time. Free meals will be served daily from 11:00 AM to 1:00 PM.', date: '2026-02-08', priority: 'medium' },
    { id: '4', title: 'Online Pooja Booking Now Available', description: 'Devotees can now book poojas online through our website. Select from a wide range of daily and special poojas. Payment can be made via UPI, credit/debit card, or net banking.', date: '2026-02-05', priority: 'medium' },
    { id: '5', title: 'Parking Area Expansion Notice', description: 'The temple parking area is being expanded to accommodate more vehicles. During construction (Feb 1-28), devotees are requested to use the temporary parking area near the community hall.', date: '2026-02-01', priority: 'medium' },
    { id: '6', title: 'Volunteer Registration Open', description: 'We are looking for dedicated volunteers to assist during the upcoming festival season. Interested devotees can register at the temple office or through our website. Training sessions will be conducted.', date: '2026-01-28', priority: 'medium' },
  ];

  const [announcements, setAnnouncements] = useState<Announcement[]>(defaultAnnouncements);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'news_section_announcements'));
      const data: Announcement[] = [];
      querySnapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() } as Announcement);
      });
      data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      if (data.length > 0) {
        setAnnouncements(data);
      }
    } catch (error) {
      console.error('Error loading announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityConfig = (priority: string) => {
    if (priority === 'high') return { color: '#ef4444', label: 'HIGH PRIORITY', bg: 'rgba(239,68,68,0.08)' };
    return { color: '#f59e0b', label: 'MEDIUM', bg: 'rgba(245,158,11,0.08)' };
  };

  return (
    <Box sx={{ bgcolor: '#f8fafb', minHeight: '100vh' }}>
      <SEO title="Announcements — Kottayil Temple | അറിയിപ്പുകൾ" description="Latest announcements from Kottayil Temple (Kotta-Kovilakam Kshethram). Important updates, notices, and temple communications." path="/news/announcements" keywords="Kottayil temple announcements, temple updates, temple notices, Muchukunnu announcements, കൊട്ടയിൽ ക്ഷേത്രം അറിയിപ്പുകൾ" />
      <PageBanner title="Announcements" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{
              borderRadius: '28px', overflow: 'hidden', mb: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #78350f 0%, #f59e0b 40%, #fde68a 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  }}>
                    <CampaignIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2rem' }, color: '#fff', lineHeight: 1.15, mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
                      Temple Announcements
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
                      Important updates and notices for devotees
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: 'rgba(255,255,255,0.55)', lineHeight: 1.9, maxWidth: 560, mb: 2.5 }}>
                  Stay informed about official communications, circulars, and day-to-day notices from temple administration.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip icon={<CampaignIcon sx={{ fontSize: '16px !important', color: '#f59e0b !important' }} />} label="Official Updates" sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#78350f', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', '& .MuiChip-icon': { ml: '8px' } }} />
                  <Chip icon={<NotificationsActiveIcon sx={{ fontSize: '16px !important', color: '#fde68a !important' }} />} label="Priority Alerts" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)', '& .MuiChip-icon': { ml: '8px' } }} />
                </Box>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: GOLD }} />
              </Box>
            ) : announcements.length === 0 ? (
              <Box sx={{ bgcolor: '#fff', borderRadius: '24px', p: 4, border: '1px solid rgba(0,0,0,0.06)', textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.88rem', color: '#aaa' }}>No announcements available at this time.</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {announcements.map((item) => {
                  const pc = getPriorityConfig(item.priority);
                  const gradient = item.priority === 'high' ? ['#ef4444', '#dc2626'] : ['#f59e0b', '#d97706'];
                  return (
                    <Box key={item.id} sx={{
                      bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                      border: '1px solid rgba(0,0,0,0.06)',
                      boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                      transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 16px 48px rgba(0,0,0,0.08)' },
                    }}>
                      {/* Gradient header */}
                      <Box sx={{ background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`, px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: '12px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid rgba(255,255,255,0.2)' }}>
                          {item.priority === 'high' ? (
                            <PriorityHighIcon sx={{ fontSize: 18, color: '#fff' }} />
                          ) : (
                            <InfoIcon sx={{ fontSize: 18, color: '#fff' }} />
                          )}
                        </Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#fff', flex: 1 }}>
                          {item.title}
                        </Typography>
                        <Box sx={{ px: 1.2, py: 0.3, borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)' }}>
                          <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#fff', textTransform: 'uppercase' }}>
                            {pc.label}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                          <CalendarMonthIcon sx={{ fontSize: 12, color: gradient[0] }} />
                          <Typography sx={{ fontSize: '0.65rem', color: '#999', fontWeight: 600 }}>{item.date}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.82rem', color: '#666', lineHeight: 1.9 }}>
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="news" activePath="/news/announcements" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
